'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@brew/ui/sheet'
import { SignInButton, SignUpButton, useAuth } from '@clerk/nextjs'
import { ExternalLink, Menu, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  MARKETING_NAV_CANVAS_BG,
  MARKETING_NAV_CANVAS_SHADOW,
  MARKETING_NAV_HEADER_MAX_SCROLLED,
  MARKETING_NAV_HEADER_MAX_TOP,
  MARKETING_NAV_MOBILE_MARGIN_SCROLLED,
  MARKETING_NAV_MOBILE_MARGIN_TOP,
  MARKETING_NAV_SCROLL_THRESHOLD,
  MARKETING_NAV_SCROLLED_BG,
  MARKETING_NAV_SCROLLED_SHADOW,
  MARKETING_NAV_TOP_BG,
  MARKETING_NAV_TOP_OFFSET,
  MARKETING_NAV_TRANSPARENT_SHADOW,
} from './marketing-layout'
import { PillButton } from './pill-button'

const NAV_LINKS: Array<{
  href: string
  label: string
  external?: boolean
}> = [
  { href: '/pricing', label: 'Pricing' },
  { href: '/browse/templates', label: 'Templates' },
  { href: '/browse/integrations', label: 'Integrations' },
  { href: '/mcp', label: 'MCP' },
  { href: '/blog', label: 'Blog' },
  { href: 'https://docs.brew.new/', label: 'Docs', external: true },
]

function NavLink({
  href,
  label,
  external,
}: {
  href: string
  label: string
  external?: boolean
}) {
  const linkProps = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {}
  return (
    <Link
      href={href}
      className="group relative rounded-lg px-3 py-2 font-normal text-foreground/70 text-sm transition-colors hover:text-primary dark:text-gray-200/80 dark:hover:text-orange-400"
      {...linkProps}
    >
      {label}
      <span className="absolute inset-x-1 -bottom-0.5 h-px scale-x-0 bg-gradient-to-r from-orange-500/0 via-orange-400 to-orange-500/0 opacity-0 transition duration-150 group-hover:scale-x-100 group-hover:opacity-100" />
      <span className="pointer-events-none absolute inset-0 origin-bottom scale-0 overflow-hidden opacity-0 transition duration-150 group-hover:scale-100 group-hover:opacity-100">
        <span className="absolute inset-x-4 -bottom-2 h-full rounded-t-full bg-gradient-to-t from-orange-500/20 to-transparent blur" />
      </span>
    </Link>
  )
}

/**
 * Marketing navbar for `app/(public)/layout.tsx`. At the top of the page the
 * bar is transparent and inset; after scroll it becomes a centered rounded
 * floating island (same motion model as `components/navbar.tsx`). Keeps v2
 * link styling and ink pill CTAs.
 *
 * `/explore/*` routes use a flush opaque bar so the canvas cannot read
 * through the nav.
 */
export function NavbarV2() {
  const [hasScrolled, setHasScrolled] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { isSignedIn } = useAuth()
  const pathname = usePathname()
  const isCanvasRoute = pathname?.startsWith('/explore') ?? false

  useEffect(() => {
    const syncHasScrolled = () => {
      setHasScrolled(window.scrollY > MARKETING_NAV_SCROLL_THRESHOLD)
    }

    syncHasScrolled()
    window.addEventListener('scroll', syncHasScrolled, { passive: true })
    return () => window.removeEventListener('scroll', syncHasScrolled)
  }, [])

  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev)
  const handleCloseDrawer = () => setIsDrawerOpen(false)

  let headerMaxWidth: number
  if (isCanvasRoute) {
    headerMaxWidth = 9999
  } else if (hasScrolled) {
    headerMaxWidth = MARKETING_NAV_HEADER_MAX_SCROLLED
  } else {
    headerMaxWidth = MARKETING_NAV_HEADER_MAX_TOP
  }

  let navBackground: string
  if (isCanvasRoute) {
    navBackground = MARKETING_NAV_CANVAS_BG
  } else if (hasScrolled) {
    navBackground = MARKETING_NAV_SCROLLED_BG
  } else {
    navBackground = MARKETING_NAV_TOP_BG
  }

  let navShadow: string
  if (isCanvasRoute) {
    navShadow = MARKETING_NAV_CANVAS_SHADOW
  } else if (hasScrolled) {
    navShadow = MARKETING_NAV_SCROLLED_SHADOW
  } else {
    navShadow = MARKETING_NAV_TRANSPARENT_SHADOW
  }

  let mobileMargin: number
  if (isCanvasRoute) {
    mobileMargin = 0
  } else if (hasScrolled) {
    mobileMargin = MARKETING_NAV_MOBILE_MARGIN_SCROLLED
  } else {
    mobileMargin = MARKETING_NAV_MOBILE_MARGIN_TOP
  }

  return (
    <>
      {/*
        Dual-axis sizing, change with care:
          - `animate.maxWidth` shrinks the centered wrapper from
            MARKETING_NAV_HEADER_MAX_TOP (1216px → 1152px pill, the
            max-w-6xl guide-line column) to
            MARKETING_NAV_HEADER_MAX_SCROLLED (1000px) on scroll.
          - `md:px-8` adds a static side gutter on md+ so the pill
            never touches the viewport edge.
          - `style={{ margin: '0 auto' }}` keeps the wrapper centered
            once `maxWidth` becomes the limiting dimension.
        The desktop pill (the `nav` below) is `w-full` and lives
        inside this wrapper, so it adopts the wrapper's animated
        width minus the static padding.
      */}
      <header
        className={cn(
          'fixed right-0 left-0 z-50 flex justify-center',
          'transition-[top,max-width] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
          !isCanvasRoute && 'md:px-8'
        )}
        style={{
          margin: '0 auto',
          top: isCanvasRoute ? 0 : MARKETING_NAV_TOP_OFFSET,
          maxWidth: headerMaxWidth,
        }}
      >
        <nav
          className={cn(
            'pointer-events-auto hidden h-14 w-full items-center justify-between gap-4 px-5 lg:flex',
            'transition-[background-color,box-shadow] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
            isCanvasRoute
              ? 'border-border border-b shadow-md'
              : cn(
                  'rounded-full border-[0.5px] backdrop-blur-xl',
                  hasScrolled
                    ? 'border-border dark:border-white/[0.12]'
                    : 'border-border dark:border-white/10'
                )
          )}
          style={{
            backgroundColor: navBackground,
            boxShadow: navShadow,
          }}
        >
          <Link
            href="/"
            className="flex shrink-0 items-center"
            aria-label="Brew home"
          >
            <Image
              src="/logo.svg"
              alt="Brew"
              width={80}
              height={24}
              className="h-6 w-20 object-contain dark:invert"
              priority
            />
          </Link>

          <ul className="flex flex-1 items-center justify-center gap-1">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <NavLink {...link} />
              </li>
            ))}
          </ul>

          <div className="flex shrink-0 items-center gap-2">
            {isSignedIn ? (
              <PillButton
                variant="ghost"
                href="/home"
                className="dark:border-white/20 dark:bg-white/10 dark:text-gray-200 dark:shadow-[0_1px_0_rgba(255,255,255,0.08),0_8px_24px_-18px_rgba(0,0,0,0.9)] dark:hover:border-orange-400/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-400"
              >
                Dashboard
              </PillButton>
            ) : (
              <>
                <SignInButton mode="modal">
                  <button
                    type="button"
                    className="h-9 cursor-pointer rounded-lg px-3 font-medium text-foreground/80 text-sm transition-colors hover:text-primary dark:border dark:border-white/20 dark:bg-white/10 dark:text-gray-200 dark:shadow-[0_1px_0_rgba(255,255,255,0.08),0_8px_24px_-18px_rgba(0,0,0,0.9)] dark:hover:border-orange-400/30 dark:hover:bg-orange-500/10 dark:hover:text-orange-400"
                  >
                    Sign in
                  </button>
                </SignInButton>
                <SignUpButton mode="modal">
                  <button
                    type="button"
                    className="inline-flex h-9 cursor-pointer items-center justify-center rounded-lg bg-primary px-4 font-medium text-primary-foreground text-sm shadow-sm transition-colors hover:bg-primary/90 dark:bg-gradient-to-r dark:from-orange-600 dark:to-orange-500 dark:text-white dark:shadow-[0_10px_26px_-14px_rgba(255,119,56,0.9)] dark:hover:from-orange-700 dark:hover:to-orange-600"
                  >
                    Get started
                  </button>
                </SignUpButton>
              </>
            )}
          </div>
        </nav>

        <div
          className={cn(
            'flex w-full items-center justify-between gap-3 px-4 py-1 lg:hidden',
            'transition-[background-color,box-shadow,margin] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]',
            isCanvasRoute
              ? 'border-border border-b shadow-md'
              : cn(
                  'rounded-full border-[0.5px] backdrop-blur-xl',
                  hasScrolled
                    ? 'border-border dark:border-white/[0.12]'
                    : 'border-border dark:border-white/10'
                )
          )}
          style={{
            backgroundColor: navBackground,
            boxShadow: navShadow,
            marginLeft: mobileMargin,
            marginRight: mobileMargin,
          }}
        >
          <Link href="/" className="py-2" aria-label="Brew home">
            <Image
              src="/logo.svg"
              alt="Brew"
              width={80}
              height={24}
              className="h-6 w-20 object-contain dark:invert"
              priority
            />
          </Link>

          <button
            type="button"
            className="flex size-9 cursor-pointer items-center justify-center rounded-lg text-foreground transition-transform hover:scale-105 active:scale-95"
            onClick={toggleDrawer}
            aria-label={isDrawerOpen ? 'Close menu' : 'Open menu'}
          >
            {isDrawerOpen ? (
              <X className="size-5" />
            ) : (
              <Menu className="size-5" />
            )}
          </button>
        </div>
      </header>

      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          overlayClassName="z-40 backdrop-blur-xs"
          className="bottom-3 z-40 mx-auto w-[95%] rounded-xl border border-border p-4"
        >
          <SheetHeader className="flex-row items-center justify-between p-0 text-muted-foreground">
            <SheetTitle className="font-medium text-muted-foreground text-sm">
              Menu
            </SheetTitle>
            <SheetClose
              aria-label="Close"
              className="cursor-pointer rounded-lg p-1 transition-transform hover:scale-110 active:scale-95"
            >
              <X className="size-5" />
            </SheetClose>
          </SheetHeader>
          <ul className="mb-4 flex flex-col rounded-xl border border-border text-sm">
            {NAV_LINKS.map((link, index) => (
              <li
                key={link.href}
                className={cn('p-3', {
                  'border-border border-b': index < NAV_LINKS.length - 1,
                })}
              >
                <Link
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={handleCloseDrawer}
                  className="flex items-center gap-1 text-foreground/80 transition-colors hover:text-foreground"
                >
                  {link.label}
                  {link.external ? (
                    <ExternalLink className="size-3 opacity-60" />
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>

          {!isSignedIn ? (
            <div className="flex gap-2">
              <SignInButton mode="modal">
                <Button
                  onClick={handleCloseDrawer}
                  variant="outline"
                  className="h-11 flex-1 cursor-pointer rounded-full"
                >
                  Sign in
                </Button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button
                  type="button"
                  onClick={handleCloseDrawer}
                  className="h-11 flex-1 cursor-pointer rounded-full bg-primary font-medium text-primary-foreground text-sm shadow-sm transition-colors hover:bg-primary/90"
                >
                  Get started
                </button>
              </SignUpButton>
            </div>
          ) : (
            <Button
              asChild
              variant="outline"
              className="h-11 flex-1 rounded-full"
            >
              <Link href="/home" onClick={handleCloseDrawer}>
                Dashboard
              </Link>
            </Button>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}
